import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const EarningsSection = () => {
  const [currentRevenue, setCurrentRevenue] = useState(85000);
  const [animatedEarnings, setAnimatedEarnings] = useState(0);
  const [selectedTimeframe, setSelectedTimeframe] = useState('annual');

  const projectedIncrease = 13000;
  const newRevenue = currentRevenue + projectedIncrease;

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimatedEarnings(newRevenue);
    }, 500);
    return () => clearTimeout(timer);
  }, [newRevenue]);

  const earningsBreakdown = {
    monthly: {
      current: Math.round(currentRevenue / 12),
      projected: Math.round(newRevenue / 12),
      increase: Math.round(projectedIncrease / 12)
    },
    annual: {
      current: currentRevenue,
      projected: newRevenue,
      increase: projectedIncrease
    }
  };

  const contractorSuccess = [
    {
      name: "Carlos Martinez",
      trade: "HVAC Specialist",
      location: "Tampa",
      before: "$78,000",
      after: "$94,500",
      increase: "+$16,500",
      timeframe: "12 months",
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
      quote: "ProConnect changed everything. Premium customers, guaranteed payments, zero marketing headaches."
    },
    {
      name: "Jennifer Walsh",
      trade: "Master Plumber",
      location: "St. Petersburg",
      before: "$92,000",
      after: "$108,200",
      increase: "+$16,200",
      timeframe: "10 months",
      avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face",
      quote: "Best decision for my business. Higher-paying jobs and customers who actually value quality work."
    },
    {
      name: "Robert Kim",
      trade: "Licensed Electrician",
      location: "Clearwater",
      before: "$85,000",
      after: "$103,800",
      increase: "+$18,800",
      timeframe: "14 months",
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
      quote: "Doubled my profit margins. No more competing on price - customers choose quality."
    }
  ];

  const seasonalData = [
    { month: 'Jan', current: 6800, projected: 8200 },
    { month: 'Feb', current: 7200, projected: 8600 },
    { month: 'Mar', current: 8100, projected: 9500 },
    { month: 'Apr', current: 8900, projected: 10300 },
    { month: 'May', current: 9200, projected: 10800 },
    { month: 'Jun', current: 9800, projected: 11400 },
    { month: 'Jul', current: 10200, projected: 11900 },
    { month: 'Aug', current: 9900, projected: 11600 },
    { month: 'Sep', current: 8700, projected: 10200 },
    { month: 'Oct', current: 8200, projected: 9600 },
    { month: 'Nov', current: 7500, projected: 8800 },
    { month: 'Dec', current: 6900, projected: 8100 }
  ];

  const maxValue = Math.max(...seasonalData?.map(d => d?.projected));

  return (
    <section id="earnings" className="py-16 lg:py-24 bg-muted/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-6">
            See Your Earnings Potential
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Calculate how much more you could earn with ProConnect's premium marketplace. 
            Real contractors are seeing $13,000+ annual increases.
          </p>
        </div>

        {/* Interactive Earnings Calculator */}
        <div className="bg-card rounded-xl p-8 border border-border shadow-testimonial mb-16">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Left Side - Calculator */}
            <div>
              <h3 className="text-2xl font-bold text-foreground mb-6">
                Your Earnings Calculator
              </h3>
              
              {/* Current Revenue Slider */}
              <div className="mb-8">
                <label className="block text-sm font-medium text-foreground mb-3">
                  Current Annual Revenue
                </label>
                <div className="relative">
                  <input
                    type="range"
                    min="40000"
                    max="200000"
                    step="5000"
                    value={currentRevenue}
                    onChange={(e) => setCurrentRevenue(Number(e?.target?.value))}
                    className="w-full h-3 bg-muted rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between text-sm text-muted-foreground mt-2">
                    <span>$40K</span>
                    <span className="font-bold text-primary text-lg">
                      ${currentRevenue?.toLocaleString()}
                    </span>
                    <span>$200K</span>
                  </div>
                </div>
              </div>

              {/* Timeframe Toggle */}
              <div className="mb-8">
                <div className="flex bg-muted rounded-lg p-1">
                  <button
                    onClick={() => setSelectedTimeframe('monthly')}
                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                      selectedTimeframe === 'monthly' ?'bg-primary text-primary-foreground' :'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    Monthly View
                  </button>
                  <button
                    onClick={() => setSelectedTimeframe('annual')}
                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                      selectedTimeframe === 'annual' ?'bg-primary text-primary-foreground' :'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    Annual View
                  </button>
                </div>
              </div>

              {/* Earnings Comparison */}
              <div className="space-y-4">
                <div className="flex justify-between items-center p-4 bg-muted/50 rounded-lg">
                  <span className="text-foreground font-medium">Current Earnings</span>
                  <span className="text-xl font-bold text-muted-foreground">
                    ${earningsBreakdown?.[selectedTimeframe]?.current?.toLocaleString()}
                  </span>
                </div>
                
                <div className="flex justify-between items-center p-4 bg-primary/10 border border-primary/20 rounded-lg">
                  <span className="text-foreground font-medium">With ProConnect</span>
                  <span className="text-2xl font-bold text-primary">
                    ${earningsBreakdown?.[selectedTimeframe]?.projected?.toLocaleString()}
                  </span>
                </div>
                
                <div className="flex justify-between items-center p-4 bg-accent/10 border border-accent/20 rounded-lg">
                  <span className="text-foreground font-medium">Your Increase</span>
                  <span className="text-2xl font-bold text-accent">
                    +${earningsBreakdown?.[selectedTimeframe]?.increase?.toLocaleString()}
                  </span>
                </div>
              </div>

              {/* Benefits Breakdown */}
              <div className="mt-8 p-6 bg-secondary/5 border border-secondary/20 rounded-lg">
                <h4 className="font-semibold text-foreground mb-4">How You'll Earn More:</h4>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <Icon name="TrendingUp" size={16} className="text-secondary mr-3" />
                    <span className="text-sm text-foreground">Higher average job values ($2,847 vs $1,200)</span>
                  </div>
                  <div className="flex items-center">
                    <Icon name="Users" size={16} className="text-secondary mr-3" />
                    <span className="text-sm text-foreground">Premium customers who pay on time</span>
                  </div>
                  <div className="flex items-center">
                    <Icon name="DollarSign" size={16} className="text-secondary mr-3" />
                    <span className="text-sm text-foreground">Zero marketing expenses (save $30K+/year)</span>
                  </div>
                  <div className="flex items-center">
                    <Icon name="Clock" size={16} className="text-secondary mr-3" />
                    <span className="text-sm text-foreground">More billable hours, less admin work</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side - Seasonal Chart */}
            <div>
              <h3 className="text-2xl font-bold text-foreground mb-6">
                Monthly Earnings Projection
              </h3>
              
              <div className="bg-background rounded-lg p-6 border border-border">
                <div className="space-y-4">
                  {seasonalData?.map((data, index) => (
                    <div key={index} className="flex items-center">
                      <div className="w-8 text-sm text-muted-foreground">{data?.month}</div>
                      <div className="flex-1 mx-4">
                        <div className="relative h-8 bg-muted rounded">
                          {/* Current earnings bar */}
                          <div
                            className="absolute top-0 left-0 h-full bg-muted-foreground/40 rounded"
                            style={{ width: `${(data?.current / maxValue) * 100}%` }}
                          />
                          {/* Projected earnings bar */}
                          <div
                            className="absolute top-0 left-0 h-full bg-primary rounded"
                            style={{ width: `${(data?.projected / maxValue) * 100}%` }}
                          />
                        </div>
                      </div>
                      <div className="w-20 text-right">
                        <div className="text-sm font-bold text-primary">
                          ${data?.projected?.toLocaleString()}
                        </div>
                        <div className="text-xs text-accent">
                          +${(data?.projected - data?.current)?.toLocaleString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="mt-6 flex items-center justify-center space-x-6 text-sm">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-muted-foreground/40 rounded mr-2" />
                    <span className="text-muted-foreground">Current</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-primary rounded mr-2" />
                    <span className="text-foreground">With ProConnect</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Success Stories */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-foreground text-center mb-12">
            Real Contractor Success Stories
          </h3>
          
          <div className="grid md:grid-cols-3 gap-8">
            {contractorSuccess?.map((contractor, index) => (
              <div key={index} className="bg-card rounded-xl p-6 border border-border shadow-testimonial">
                <div className="flex items-center mb-4">
                  <img
                    src={contractor?.avatar}
                    alt={contractor?.name}
                    className="w-12 h-12 rounded-full object-cover mr-4"
                  />
                  <div>
                    <div className="font-semibold text-foreground">{contractor?.name}</div>
                    <div className="text-sm text-muted-foreground">{contractor?.trade}</div>
                    <div className="text-xs text-muted-foreground">{contractor?.location}</div>
                  </div>
                </div>
                
                <blockquote className="text-sm text-foreground italic mb-4">
                  "{contractor?.quote}"
                </blockquote>
                
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Before:</span>
                    <span className="text-sm font-medium text-foreground">{contractor?.before}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">After:</span>
                    <span className="text-sm font-bold text-primary">{contractor?.after}</span>
                  </div>
                  <div className="flex justify-between border-t border-border pt-2">
                    <span className="text-sm font-medium text-foreground">Increase:</span>
                    <span className="text-lg font-bold text-accent">{contractor?.increase}</span>
                  </div>
                  <div className="text-xs text-muted-foreground text-center">
                    in {contractor?.timeframe}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Market Comparison */}
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-8 text-center">
          <h3 className="text-2xl font-bold text-foreground mb-4">
            Tampa Bay Market Average
          </h3>
          <div className="grid sm:grid-cols-3 gap-8">
            <div>
              <div className="text-3xl font-bold text-primary mb-2">$126,542</div>
              <div className="text-sm text-muted-foreground">ProConnect Contractor Average</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-muted-foreground mb-2">$89,200</div>
              <div className="text-sm text-muted-foreground">Industry Average</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-accent mb-2">+42%</div>
              <div className="text-sm text-muted-foreground">ProConnect Advantage</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EarningsSection;